
import { useState, useCallback } from 'react';
import { DataConnector, StandardizedConnection } from '@/types/connections';
import { fetchDataConnectors, createDataConnection } from '@/utils/api';
import { toast } from 'sonner';
import { saveFormDataWithToken, updateFormToken } from '@/utils/formTokenManager';

export const useConnectorState = (connectorId?: string, onConnectorUpdate?: (connectors: StandardizedConnection[]) => void) => {
  const [view, setView] = useState<'initial' | 'file' | 'database' | 'loading'>('initial');
  const [connectors, setConnectors] = useState<DataConnector[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<DataConnector | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [shopName, setShopName] = useState('');

  const loadConnectors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const availableConnectors = await fetchDataConnectors();
      if (availableConnectors && availableConnectors.length > 0) {
        setConnectors(availableConnectors);
        setView('database');
      } else {
        setError('No connectors available to connect to');
        setTimeout(() => {
          setError(null);
        }, 5000);
      }
    } catch (err) {
      toast.error('Failed to load available connectors');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConnect = async () => {
    if (!selectedConnector) return;

    try {
      setIsConnecting(true);

      // Save current route step before redirecting
      const currentPath = window.location.pathname;
      const stepMatch = currentPath.match(/\/step\/(\d+)/);
      if (stepMatch && stepMatch[1]) {
        sessionStorage.setItem('savedFormStep', stepMatch[1]);
      }

      // Save form data to server and get token FIRST, before calling the edge function
      const token = await saveFormDataWithToken();

      if (!token) {
        toast.error('Failed to save form data. Please try again.');
        setIsConnecting(false);
        return;
      }

      // Build the full redirect URI with form token — this gets baked into the Fivetran Connect Card URL
      const fullRedirectUri = `${window.location.origin}/step/2?form_token=${token}`;

      const isShopify = selectedConnector.id === 'shopify' || selectedConnector.name === 'Shopify';
      const options = isShopify && shopName ? { shop: shopName } : undefined;
      const response = await createDataConnection(selectedConnector, fullRedirectUri, options);

      if (response && response.data && response.data.url) {
        // Store the redirect info with the token
        sessionStorage.setItem('connectorRedirect', 'true');
        sessionStorage.setItem('selectedConnectorId', response.data.connectorId);
        sessionStorage.setItem('selectedConnectionId', response.data.connectionId);

        // Re-save the token so it includes the new connectionMap entry
        await updateFormToken(token);

        // Navigate directly — redirect_uri is already baked into the Fivetran Connect Card URL
        window.location.href = response.data.url;
      } else {
        toast.error('No redirect URL provided in the response');
        setIsConnecting(false);
      }
    } catch (err) {
      console.error('Error connecting to data source:', err);
      toast.error('Failed to connect to data source');
      setIsConnecting(false);
    }
  };

  return {
    view,
    setView,
    connectors,
    loading,
    selectedConnector,
    setSelectedConnector,
    error,
    isConnecting,
    shopName,
    setShopName,
    loadConnectors,
    handleConnect
  };
};
